import React, { useEffect, useState } from 'react';
import styles from '@/css/module/new-world.css';
import { useSelector } from 'react-redux';
import { Footer } from '@/views/QueryBuilder/subcomponents';
import { EXPLORER } from '@/util/enums/views.constants';
import ResizeableSidebar from '@/components/ResizeableSidebar';
import { completed } from '@/actions/helpers';
import { PublicDatasetsDropdown } from '@/features/publicDatasets/publicdatasets/PublicDatasetsDropdown';
import { WelcomeMessage } from '@/features/publicDatasets/publicdatasets/WelcomeMessage';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import AjaxSpinner from '@/components/AjaxSpinner';
import PublicDatasetQueryName from '@/features/publicDatasets/PublicDatasetQueryName';
import { useGetRowRequestMetadataQuery } from '@/features/publicDatasets/publicDatasetApi';
import { SharedRowRequestMetadata } from '@/features/publicDatasets/types';
import PublicDatasetProcessHints from '@/features/publicDatasets/PublicDatasetProcessHints';

import { TABLES_BUTTON } from '@/components/StartNewQuery/buttons.constants';
import { loadTables, changeTab, rowsInvalidated } from '@/actions';
import {
  CHANGE_DATASOURCE,
  SHARE_METADATA_LOADED,
  DATASOURCE_SELECTED,
  USER_ACTIONS_LOADED,
  SHARE_METADATA_CHANGED,
} from '@/actions/constants';
// @ts-expect-error: Update to the latest version - written in TS.
import cookie from 'react-cookie';
import { getQueryBuilderState } from '@/features/publicDatasets/selectors';
import { DataPresentationNavigation } from '@/features/dataPresentation/navigation/DataPresentationNavigation';
import { QueryConfigSidebar } from '@/features/queryConfig/components/QueryConfigSidebar';
import {
  getCollapsedLayerId,
  getIsLoadingView,
  getLayerInEditMode,
  getPage,
  getQueryBuilderSteps,
  getSelectedLayerId,
} from '@/store-selectors';
import LayerHelper from '@/util/LayerHelper';
import {
  CSV_FROM,
  FROM,
  FROM_SAVED_QUERY,
  SQL_FROM,
} from '@/util/enums/EnumLayers';
import { foldPredicates } from '@/util/fp';
import Layer from '@/components/layers/Layer';
import { useAppDispatch } from '@/store/hooks';
import { userIsFinishedOnboarding } from '@/components/HighlightFeature';
import stylesComponent from './PublicDatasetQueryBuilder.module.scss';
import ErrorBoundary from '../components/ErrorBoundary';
import DataPresentationContainer from './DataPresentationContainer';
import StepsSidebar from './StepsSidebar';

type Step = Layer;

const FIRST_STEP_TYPES = [FROM, FROM_SAVED_QUERY, SQL_FROM, CSV_FROM];

const hashSteps = (steps: ReturnType<typeof getQueryBuilderSteps>) => {
  try {
    return btoa(JSON.stringify(steps));
  } catch (e) {
    return JSON.stringify(steps);
  }
};

const isNotTemplate = (s: Step) => !s.isTemplate();
const isEnabled = (s: Step) => s._enabled;

const isRowRequestAffectable = foldPredicates.withAnd(isNotTemplate, isEnabled);

// TODO: this hook will be removed after migration to react-router is complete.
export const useQueryBuilderFlow_LEGACY = () => {
  const dispatch = useAppDispatch();

  const steps = useSelector(getQueryBuilderSteps);
  const currQueryBuilderPage = useSelector(getPage);
  const collapsedLayerId = useSelector(getCollapsedLayerId);
  const layerInEditMode = useSelector(getLayerInEditMode);
  const selectedStepId = useSelector(getSelectedLayerId);
  const isLoadingView = useSelector(getIsLoadingView);
  const stepsHashRef = React.useRef('');
  const prevSelectedStepIdRef = React.useRef(selectedStepId);
  const prevQueryBuilderPage = React.useRef(currQueryBuilderPage);

  React.useEffect(() => {
    const selectedStep = steps.find((s) => s._id === selectedStepId);
    const lastSelectedStepIndex = steps.findIndex(
      (s) => s._id === selectedStepId,
    );
    const newStepsHash = hashSteps(
      steps.slice(0, lastSelectedStepIndex + 1).filter(isRowRequestAffectable),
    );

    const stepsHaveChanged =
      steps.length && newStepsHash !== stepsHashRef.current;

    const selectedStepHasChanged =
      selectedStepId !== prevSelectedStepIdRef.current;

    const selectedStepAffectsRowRequest =
      selectedStep &&
      isRowRequestAffectable(selectedStep) &&
      !FIRST_STEP_TYPES.includes(selectedStep._type);

    const pageHasChanged =
      currQueryBuilderPage !== prevQueryBuilderPage.current;

    if (
      stepsHaveChanged ||
      pageHasChanged ||
      (selectedStepHasChanged && selectedStepAffectsRowRequest)
    ) {
      const layerHelper = new LayerHelper(
        steps,
        selectedStepId,
        collapsedLayerId,
      );
      const enabledLayers = layerHelper.enabledLayers();

      dispatch(
        rowsInvalidated(
          enabledLayers,
          isLoadingView,
          'browser',
          layerInEditMode,
        ),
      );

      stepsHashRef.current = newStepsHash;
      prevSelectedStepIdRef.current = selectedStepId;
      prevQueryBuilderPage.current = currQueryBuilderPage;
    }
  }, [
    steps,
    selectedStepId,
    collapsedLayerId,
    layerInEditMode,
    isLoadingView,
    currQueryBuilderPage,
  ]);
};

const Placeholder = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const USER_ACTIONS_COOKIE = 'public-dataset-user-actions';

export const PublicDatasetQueryBuilder: React.FC = () => {
  const {
    showChartingOptions,
    layers,
    rowRequestId,
    tablesLoaded,
    userActions,
  } = useSelector(getQueryBuilderState);

  const [getStarted, setStarted] = useState(true);

  const [rowRequest, setRowRequest] = useState<Nullable<string>>();

  const { datasourceId, rowRequestId: initialRowRequest } = useParams();

  const dispatch = useAppDispatch();

  useQueryBuilderFlow_LEGACY();

  const loadUserActions = () => {
    const cookieActions = cookie.load(USER_ACTIONS_COOKIE);
    dispatch(completed(USER_ACTIONS_LOADED, cookieActions || []));
  };

  const saveUserActionsToCookie = () => {
    if (userActions) {
      cookie.save(
        USER_ACTIONS_COOKIE,
        JSON.stringify(Object.keys(userActions)),
        {
          path: '/',
        },
      );
    }
  };

  const onLoad = () => {
    loadUserActions();
    dispatch(loadTables(false, false, false));
    dispatch(completed(DATASOURCE_SELECTED, { datasourceId }));
  };

  const changeDatasource = () => {
    setStarted(true);
    dispatch(completed(CHANGE_DATASOURCE));
    dispatch(completed(DATASOURCE_SELECTED, { datasourceId }));
  };

  const updateMetadata = (metadata: Nullable<SharedRowRequestMetadata>) => {
    dispatch(
      completed(SHARE_METADATA_LOADED, {
        metadata,
      }),
    );
  };

  const _startedClicked = (value: boolean) => {
    setStarted(value);
    setRowRequest(null);
    updateMetadata(null);
    dispatch(changeTab(EXPLORER));
    if (initialRowRequest) {
      dispatch(
        completed(SHARE_METADATA_CHANGED, {
          metadataChanged: true,
        }),
      );
    }
  };

  useGetRowRequestMetadataQuery(
    {
      datasourceId,
      rowRequestId: initialRowRequest!,
    },
    { skip: !datasourceId || !initialRowRequest },
  );

  useEffect(() => onLoad(), [datasourceId]);
  useEffect(() => {
    setRowRequest(initialRowRequest);
  }, [initialRowRequest]);
  useEffect(saveUserActionsToCookie, [
    userActions ? JSON.stringify(Object.keys(userActions)) : '',
  ]);

  // show spinner if the page is not ready yet
  if (!tablesLoaded) {
    return (
      <Placeholder>
        <AjaxSpinner />
      </Placeholder>
    );
  }

  return (
    <>
      <ResizeableSidebar
        initialWidth={430}
        isVisible={!showChartingOptions}
        className={stylesComponent.sideBar}
        isToggleShown
      >
        <div className={stylesComponent.resetQueryContainer}>
          <span className={stylesComponent.stepsHeaderText}>
            Public Datasets
          </span>
        </div>
        <PublicDatasetsDropdown onSelect={changeDatasource} />
        <StepsSidebar sidebarOpen availableButtons={[TABLES_BUTTON]} />
      </ResizeableSidebar>
      {layers.length === 0 && !rowRequest ? (
        <div className={styles.startMessageContainer}>
          <WelcomeMessage getStarted={getStarted} />
        </div>
      ) : (
        <>
          <div className={styles.rightSection}>
            <PublicDatasetQueryName
              rowRequestId={rowRequestId}
              datasourceId={datasourceId}
              initialRowRequest={initialRowRequest}
            />
            <div
              className={`${styles.tableContainer} restpackTableScreenshotSelector`}
            >
              {userIsFinishedOnboarding(userActions) && (
                <DataPresentationNavigation />
              )}

              <DataPresentationContainer />
            </div>
            <Footer />
          </div>
          <ResizeableSidebar
            initialWIdth={375}
            isVisible={showChartingOptions}
            isRightSidebar
            isToggleShown
          >
            <ErrorBoundary key={`${showChartingOptions}`}>
              <QueryConfigSidebar showOnboarding={showChartingOptions} />
            </ErrorBoundary>
          </ResizeableSidebar>
        </>
      )}
      {!showChartingOptions && <PublicDatasetProcessHints />}
    </>
  );
};
